import { isWeb } from '@helpers';

/**
 * Resolves a CSS selector to an actual visible DOM element for driver.js.
 *
 * ## Problem
 *
 * React Native Web renders `flex: 1` as CSS `flex: 1 1 0%`. Inside a
 * ScrollView (e.g. MachinesScreenContentWrapper's Animated.ScrollView), elements with
 * `flex-basis: 0%` collapse to zero dimensions. The content overflows
 * and IS visible on screen (thanks to CSS `overflow: visible`), but
 * `getBoundingClientRect()` returns `{ x: 0, y: 0, width: 0, height: 0 }`.
 *
 * Driver.js uses `getBoundingClientRect()` to position the tooltip and
 * highlight overlay. When it gets all zeros, it renders at the top-left
 * corner of the viewport.
 *
 * This affects ALL elements inside the MachinesProvider → MachinesScreenContentWrapper →
 * ScrollView chain on the Machines page, including any `TourTarget` wrappers
 * placed there. The parent chain collapses to zero; fixing only the
 * TourTarget's flex properties does not help because parents also have
 * zero dimensions.
 *
 * ## Solution
 *
 * 1. Query all elements matching the selector
 * 2. If any match has non-zero dimensions, return it (fast path)
 * 3. Otherwise, find the **largest visible descendant** inside the
 *    zero-dimension element and return that instead
 *
 * Driver.js accepts both CSS selector strings and `HTMLElement` references,
 * so passing the resolved element works directly.
 *
 * ## Usage
 *
 * ```typescript
 * // In buildDriverSteps:
 * element: resolveVisibleElement(`[data-tour-id="${step.tourId}"]`)
 *
 * // Standalone usage:
 * const el = resolveVisibleElement('[data-tour-id="my-target"]');
 * if (el instanceof HTMLElement) {
 *   // el has real dimensions — use for positioning
 * }
 * ```
 *
 * @param selector - CSS selector string
 * @returns The resolved HTMLElement with real dimensions, or the original
 *          selector string as fallback
 */
export const resolveVisibleElement = (selector: string): HTMLElement | string => {
  if (!isWeb) return selector;

  const elements = document.querySelectorAll(selector);

  // First pass: find a match that already has real dimensions
  for (const el of elements) {
    const rect = el.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) {
      return el as HTMLElement;
    }
  }

  // Second pass: for zero-dimension matches, find the largest visible descendant.
  // The TourTarget wraps content that renders visibly (via CSS overflow) even though
  // the wrapper itself has zero dimensions. The descendant with the largest area
  // is the best approximation of what the user intended to highlight.
  for (const el of elements) {
    const descendants = el.querySelectorAll('*');
    let bestMatch: HTMLElement | null = null;
    let bestArea = 0;

    for (const desc of descendants) {
      const rect = desc.getBoundingClientRect();
      const area = rect.width * rect.height;
      if (area > bestArea) {
        bestArea = area;
        bestMatch = desc as HTMLElement;
      }
    }

    if (bestMatch) return bestMatch;
  }

  // Fallback: return the CSS selector and let driver.js handle it
  return selector;
};
